import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { BsActivity, BsArrowClockwise, BsFilter, BsPersonCircle, BsChat, BsFolder, BsCalendar, BsGear, BsPeople, BsCreditCard, BsClipboard } from 'react-icons/bs';

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  border-left: 1px solid rgba(100, 150, 200, 0.15);
`;

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(100, 150, 200, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(15, 20, 25, 0.8);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: #8b5cf6;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: #a78bfa;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LogList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.3);
    border-radius: 3px;
  }
`;

const LogItem = styled.div`
  padding: 12px;
  margin-bottom: 8px;
  background: rgba(26, 31, 46, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.1);
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(26, 31, 46, 0.9);
    border-color: rgba(139, 92, 246, 0.2);
  }
`;

const LogHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const CategoryIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$color || 'rgba(139, 92, 246, 0.15)'};
  color: ${props => props.$iconColor || '#a78bfa'};
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActorName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #e2e8f0;
`;

const Timestamp = styled.span`
  font-size: 11px;
  color: #64748b;
  margin-left: auto;
`;

const Description = styled.p`
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.4;
`;

const TargetBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 6px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
  text-align: center;
  padding: 40px;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
  
  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid rgba(139, 92, 246, 0.2);
    border-top-color: #8b5cf6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const StatsBar = styled.div`
  padding: 8px 16px;
  background: rgba(139, 92, 246, 0.05);
  border-bottom: 1px solid rgba(100, 150, 200, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: #64748b;
`;

const DateDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0 12px;
  
  &:first-child {
    margin-top: 0;
  }
  
  span {
    font-size: 11px;
    font-weight: 600;
    color: #8b5cf6;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
  
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(139, 92, 246, 0.2);
  }
`;

// ============================================
// HELPER FUNCTIONS
// ============================================

const getCategoryConfig = (category) => {
  const configs = {
    auth: { icon: BsPersonCircle, color: 'rgba(99, 102, 241, 0.15)', iconColor: '#818cf8' },
    conversation: { icon: BsChat, color: 'rgba(34, 197, 94, 0.15)', iconColor: '#4ade80' },
    case: { icon: BsClipboard, color: 'rgba(59, 130, 246, 0.15)', iconColor: '#60a5fa' },
    document: { icon: BsFolder, color: 'rgba(245, 158, 11, 0.15)', iconColor: '#fbbf24' },
    calendar: { icon: BsCalendar, color: 'rgba(236, 72, 153, 0.15)', iconColor: '#f472b6' },
    settings: { icon: BsGear, color: 'rgba(107, 114, 128, 0.15)', iconColor: '#9ca3af' },
    team: { icon: BsPeople, color: 'rgba(139, 92, 246, 0.15)', iconColor: '#a78bfa' },
    payment: { icon: BsCreditCard, color: 'rgba(16, 185, 129, 0.15)', iconColor: '#34d399' },
    other: { icon: BsActivity, color: 'rgba(107, 114, 128, 0.15)', iconColor: '#9ca3af' }
  };
  return configs[category] || configs.other;
};

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatDateDivider = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  });
};

const groupLogsByDate = (logs) => {
  const groups = {};
  
  logs.forEach(log => {
    const date = new Date(log.timestamp).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(log);
  });
  
  return groups;
};

// ============================================
// MAIN COMPONENT
// ============================================

const ActivityLog = ({ maxItems = 500, autoRefresh = true, refreshInterval = 30000 }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const intervalRef = useRef(null);

  const fetchLogs = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await authenticatedAxios.get(`/activity-log?limit=${maxItems}`);
      
      if (response.data) {
        setLogs(response.data.logs || []);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authenticatedAxios, maxItems]);

  // Initial fetch
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchLogs(true);
      }, refreshInterval);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [autoRefresh, refreshInterval, fetchLogs]);

  const handleRefresh = () => {
    fetchLogs(true);
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title><BsActivity /> Activity Log</Title>
        </Header>
        <LoadingState>
          <div className="spinner" />
        </LoadingState>
      </Container>
    );
  }

  const groupedLogs = groupLogsByDate(logs);

  return (
    <Container>
      <Header>
        <Title><BsActivity /> Activity Log</Title>
        <HeaderActions>
          <IconButton 
            onClick={handleRefresh} 
            disabled={refreshing}
            title="Refresh"
          >
            <BsArrowClockwise style={{ 
              animation: refreshing ? 'spin 1s linear infinite' : 'none' 
            }} />
          </IconButton>
        </HeaderActions>
      </Header>
      
      <StatsBar>
        <span>Showing {logs.length} of {totalCount} activities</span>
        <span>Last 500 entries</span>
      </StatsBar>
      
      <LogList>
        {logs.length === 0 ? (
          <EmptyState>
            <BsActivity />
            <p>No activity yet</p>
            <p style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>
              Team activities will appear here
            </p>
          </EmptyState>
        ) : (
          Object.entries(groupedLogs).map(([date, dateLogs]) => (
            <React.Fragment key={date}>
              <DateDivider>
                <span>{formatDateDivider(date)}</span>
              </DateDivider>
              {dateLogs.map((log) => {
                const config = getCategoryConfig(log.category);
                const Icon = config.icon;
                
                return (
                  <LogItem key={log._id}>
                    <LogHeader>
                      <CategoryIcon $color={config.color} $iconColor={config.iconColor}>
                        <Icon />
                      </CategoryIcon>
                      <ActorName>{log.actorName}</ActorName>
                      <Timestamp>{formatTimestamp(log.timestamp)}</Timestamp>
                    </LogHeader>
                    <Description>{log.description}</Description>
                    {log.targetName && (
                      <TargetBadge>
                        {log.targetType}: {log.targetName}
                      </TargetBadge>
                    )}
                  </LogItem>
                );
              })}
            </React.Fragment>
          ))
        )}
      </LogList>
    </Container>
  );
};

export default ActivityLog;
